import { useMemo, useState } from 'react';
import { clsx } from 'clsx';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Pin } from 'lucide-react';
import { PageHeader } from '@/shared/ui';
import { Button } from '@/shared/ui/Button';
import { Input } from '@/shared/ui/Input';
import { Skeleton } from '@/shared/ui/Skeleton';
import { CreatePracticeModal } from '../components/CreatePracticeModal';
import { HealthChip } from '../components/portfolio/HealthChip';
import { STATE_META, formatAge } from '../components/portfolio/constants';
import { PracticeMenu, ViewAsMenu } from '../components/practiceActions/PracticeActions';
import { staleNeedsYou } from '../components/practiceActions/stale';
import { useSupervision } from '../hooks/useSupervision';
import { PINNED_PRACTICES_MAX, usePinnedPractices } from '../hooks/usePinnedPractices';
import type { SupervisionCard } from '../api/supervisionApi';
import { AgencyTodayPage } from './AgencyTodayPage';
import styles from './AgencyPracticesPage.module.css';
import { ReviewRequestsPanel } from '../components/ReviewRequestsPanel';

type View = 'table' | 'queue' | 'reviews';

/**
 * The agency home: one row per practice.
 *
 * Numbers and times only — anything with words is one click away on the
 * practice. Three groups, each with its own heading: the admin's own pinned
 * practices (up to three, per user), everything live, and the snoozed ones —
 * still in the table, greyed and last, so nothing is ever hidden behind a
 * fold. The cross-practice queue (the old Today page) is a view of the same
 * page rather than a second home.
 */
export function AgencyPracticesPage() {
  const [params, setParams] = useSearchParams();
  const requestedView = params.get('view');
  const view: View =
    requestedView === 'queue' || requestedView === 'reviews' ? requestedView : 'table';
  const [search, setSearch] = useState('');
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const { data, isLoading, isError } = useSupervision();
  const { data: pinnedIds } = usePinnedPractices();
  const pinned = useMemo(() => pinnedIds ?? [], [pinnedIds]);
  const summary = data?.summary;
  const cards = useMemo(() => data?.cards ?? [], [data]);

  const staleTotal = useMemo(
    () =>
      cards
        .filter((c) => !c.snoozedAt)
        .reduce((sum, c) => sum + staleNeedsYou(c.needsYou).stale.length, 0),
    [cards],
  );
  const freshNeedsYou = useMemo(
    () =>
      cards
        .filter((c) => !c.snoozedAt)
        .reduce((sum, c) => sum + staleNeedsYou(c.needsYou).fresh.length, 0),
    [cards],
  );

  const groups = useMemo(() => {
    const term = search.trim().toLowerCase();
    const matching = cards.filter((c) => (term ? c.name.toLowerCase().includes(term) : true));
    // Whoever needs the agency most first, then by name.
    const byNeed = (a: SupervisionCard, b: SupervisionCard) => {
      const needs = staleNeedsYou(b.needsYou).fresh.length - staleNeedsYou(a.needsYou).fresh.length;
      if (needs !== 0) return needs;
      return a.name.localeCompare(b.name);
    };
    const pinnedRows = pinned
      .map((id) => matching.find((c) => c.id === id))
      .filter((c): c is SupervisionCard => !!c);
    const rest = matching.filter((c) => !pinned.includes(c.id));
    return {
      pinned: pinnedRows,
      live: rest.filter((c) => !c.snoozedAt).sort(byNeed),
      snoozed: rest.filter((c) => !!c.snoozedAt).sort(byNeed),
    };
  }, [cards, search, pinned]);

  const rowCount = groups.pinned.length + groups.live.length + groups.snoozed.length;
  const liveCount = groups.pinned.filter((c) => !c.snoozedAt).length + groups.live.length;
  const snoozedCount = rowCount - liveCount;

  const setView = (next: View) => {
    const nextParams = new URLSearchParams(params);
    if (next === 'table') nextParams.delete('view');
    else nextParams.set('view', next);
    setParams(nextParams, { replace: true });
  };

  return (
    <div className={styles.page}>
      <PageHeader
        title="Practices"
        subtitle={
          summary ? (
            <span className={styles.summary}>
              <span className={clsx(styles.summaryItem, freshNeedsYou > 0 && styles.summaryHot)}>
                <b>{freshNeedsYou}</b> need you
              </span>
              <span className={styles.summaryItem}>
                <b>{summary.waitingOnClientCount}</b> waiting on clients
              </span>
              <span className={styles.summaryItem}>
                <b>{staleTotal}</b> stale, over 30 days
              </span>
              <span className={styles.summaryItem}>
                <b>{summary.actionsLastDay}</b> agent actions in the last day
              </span>
            </span>
          ) : (
            'Every practice, what needs you, and when the system last did something for them'
          )
        }
        actions={<Button onClick={() => setIsCreateOpen(true)}>+ Add Practice</Button>}
      />

      <div className={styles.controls}>
        {view === 'table' && (
          <div className={styles.searchWrap}>
            <Input
              placeholder="Search practices…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search practices"
            />
          </div>
        )}
        <div className={styles.viewSwitch} role="group" aria-label="View">
          <button
            type="button"
            className={clsx(styles.viewButton, view === 'table' && styles.viewButtonActive)}
            aria-pressed={view === 'table'}
            onClick={() => setView('table')}
          >
            Table
          </button>
          <button
            type="button"
            className={clsx(styles.viewButton, view === 'queue' && styles.viewButtonActive)}
            aria-pressed={view === 'queue'}
            onClick={() => setView('queue')}
          >
            Queue
          </button>
          <button
            type="button"
            className={clsx(styles.viewButton, view === 'reviews' && styles.viewButtonActive)}
            aria-pressed={view === 'reviews'}
            onClick={() => setView('reviews')}
          >
            Reviews
          </button>
        </div>
      </div>

      {view === 'reviews' ? (
        <ReviewRequestsPanel />
      ) : view === 'queue' ? (
        <AgencyTodayPage embedded />
      ) : (
        <>
          {isLoading && <Skeleton height={320} borderRadius="var(--card-radius-lg)" />}
          {isError && (
            <div className={styles.empty}>
              Couldn&apos;t load the practices right now. Try refreshing the page.
            </div>
          )}
          {!isLoading && !isError && (
            <div className={styles.tableScroll}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Practice</th>
                    <th>Status</th>
                    <th>Health</th>
                    <th className={styles.center}>Needs you</th>
                    <th className={styles.center}>Waiting on them</th>
                    <th className={styles.center}>Modules</th>
                    <th>Agents</th>
                    <th>Last system action</th>
                    <th>Client last login</th>
                    <th>
                      <span className={styles.srOnly}>Actions</span>
                    </th>
                  </tr>
                </thead>
                {groups.pinned.length > 0 && (
                  <tbody>
                    <GroupRow
                      className={styles.groupPinned}
                      label={`Pinned · ${groups.pinned.length}`}
                      hint={`yours only, up to ${PINNED_PRACTICES_MAX}`}
                    />
                    {groups.pinned.map((card) => (
                      <PracticeRow key={card.id} card={card} pinned pinnedCount={pinned.length} />
                    ))}
                  </tbody>
                )}
                <tbody>
                  {groups.pinned.length > 0 && groups.live.length > 0 && (
                    <GroupRow label={`All practices · ${groups.live.length}`} />
                  )}
                  {groups.live.map((card) => (
                    <PracticeRow key={card.id} card={card} pinnedCount={pinned.length} />
                  ))}
                  {rowCount === 0 && (
                    <tr>
                      <td colSpan={10} className={styles.tableEmpty}>
                        {cards.length === 0
                          ? 'No practices in your agency yet.'
                          : `No practice matches “${search}”.`}
                      </td>
                    </tr>
                  )}
                </tbody>
                {groups.snoozed.length > 0 && (
                  <tbody>
                    <GroupRow
                      className={styles.groupSnoozed}
                      label={`Snoozed · ${groups.snoozed.length}`}
                      hint="parked: out of the counts above and the practice switcher until unsnoozed"
                    />
                    {groups.snoozed.map((card) => (
                      <PracticeRow key={card.id} card={card} pinnedCount={pinned.length} />
                    ))}
                  </tbody>
                )}
              </table>
            </div>
          )}
          {!isLoading && !isError && rowCount > 0 && (
            <div className={styles.footnotes}>
              <p className={styles.note}>
                {liveCount} live · {snoozedCount} snoozed (grey, at the bottom). Sorted by what
                needs you.
              </p>
              <dl className={styles.legend}>
                <dt>Last system action</dt>
                <dd>
                  When the platform last did something for them: an agent run, audit, review request
                  or sync.
                </dd>
                <dt>Client last login</dt>
                <dd>
                  When anyone on the practice&apos;s team last signed in. “never” means nobody has.
                </dd>
              </dl>
            </div>
          )}
        </>
      )}

      <CreatePracticeModal isOpen={isCreateOpen} onClose={() => setIsCreateOpen(false)} />
    </div>
  );
}

function GroupRow({
  label,
  hint,
  className,
}: {
  label: string;
  hint?: string;
  className?: string;
}) {
  return (
    <tr className={clsx(styles.groupRow, className)}>
      <td colSpan={10}>
        {label}
        {hint && <span className={styles.groupHint}>{hint}</span>}
      </td>
    </tr>
  );
}

const PROVIDER_LABEL: Record<string, string> = { google_ads: 'Google Ads', meta_ads: 'Meta Ads' };

function PracticeRow({
  card,
  pinned = false,
  pinnedCount,
}: {
  card: SupervisionCard;
  pinned?: boolean;
  pinnedCount: number;
}) {
  const navigate = useNavigate();
  const snoozed = !!card.snoozedAt;
  const state = STATE_META[card.state];
  const { fresh, stale } = staleNeedsYou(card.needsYou);

  const syncFailed = card.syncFailed ?? [];
  const open = () => navigate(`/agency/practices/${card.id}`);

  return (
    <tr
      className={clsx(styles.row, snoozed && styles.rowSnoozed, pinned && styles.rowPinned)}
      onClick={open}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          open();
        }
      }}
    >
      <td>
        <span className={styles.name}>
          {card.name}
          {pinned && <Pin size={12} className={styles.pinIcon} aria-label="Pinned" />}
        </span>
        {card.locationsCount > 1 && (
          <span className={styles.sub}>{card.locationsCount} locations</span>
        )}
      </td>
      <td>
        {snoozed ? (
          <span className={clsx(styles.statePill, styles.muted)}>
            Snoozed {formatAge(card.snoozedAt)}
          </span>
        ) : (
          <span className={clsx(styles.statePill, styles[state.tone])}>{state.label}</span>
        )}
        {syncFailed.length > 0 && (
          <span
            className={clsx(styles.statePill, styles.syncFailed)}
            title={syncFailed
              .map(
                (f) =>
                  `${f.locationName ? `${f.locationName} · ` : ''}${PROVIDER_LABEL[f.provider] ?? f.provider}: ${f.message}`,
              )
              .join('\n')}
          >
            Sync failed
          </span>
        )}
      </td>
      <td>
        <HealthChip score={card.healthScore} />
      </td>
      <td className={styles.center}>
        <span className={clsx(styles.count, fresh.length > 0 && styles.countHot)}>
          {fresh.length || '—'}
        </span>
        {stale.length > 0 && <span className={styles.sub}>+{stale.length} stale</span>}
      </td>
      <td className={styles.center}>
        <span className={styles.count}>{card.waitingOnClient.length || '—'}</span>
      </td>
      <td className={styles.center}>
        <span className={styles.count}>{card.modules.length || '—'}</span>
      </td>
      <td>
        <span className={styles.agents}>
          {card.agents.map((agent) => (
            <span key={agent.roleKey} className={styles.agent}>
              <span
                className={clsx(styles.dot, agent.hiredLocations > 0 && styles.dotOn)}
                aria-hidden
              />
              {agent.personaName}
              <span className={styles.srOnly}>
                {agent.hiredLocations > 0 ? ' hired' : ' not hired'}
              </span>
            </span>
          ))}
          {card.agents.length === 0 && <span className={styles.sub}>—</span>}
        </span>
      </td>
      <td className={styles.time}>
        {card.lastSystemActionAt ? `${formatAge(card.lastSystemActionAt)} ago` : '—'}
      </td>
      <td className={styles.time}>
        {card.clientLastLoginAt ? `${formatAge(card.clientLastLoginAt)} ago` : 'never'}
      </td>
      <td className={styles.actionsCell} onClick={(e) => e.stopPropagation()}>
        <span className={styles.actions}>
          <ViewAsMenu practiceId={card.id} practiceName={card.name} size="sm" />
          <PracticeMenu
            practiceId={card.id}
            practiceName={card.name}
            snoozed={snoozed}
            story={card.story}
            pinned={pinned}
            pinnedCount={pinnedCount}
          />
        </span>
      </td>
    </tr>
  );
}
